import { FC, forwardRef, useImperativeHandle, useState } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, Tooltip } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import styled from 'styled-components';
import { themeConfig, useThemeContext } from '@/shared/theme';

export type ContextMenuRefProps = {
  isOpen: boolean;
};

type Props = {
  options: MenuProps['items'];
  onClick?: MenuProps['onClick'];
  ref?: React.Ref<ContextMenuRefProps>;
};

export const ContextMenu: FC<Props> = forwardRef(
  ({ options, onClick }, ref) => {
    const { theme } = useThemeContext();

    const [isOpen, setIsOpen] = useState(false);

    const handleItemClick = (menuInfo: MenuInfo) => {
      setIsOpen(false);
      onClick?.(menuInfo);
    };

    useImperativeHandle(
      ref,
      () => ({
        isOpen,
      }),
      [isOpen],
    );

    return (
      <Tooltip placement="top" title="Options" mouseEnterDelay={0.8}>
        <div style={{ cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{
              items: options,
              onClick: (menuInfo) => handleItemClick(menuInfo),
            }}
            onOpenChange={setIsOpen}
            trigger={['click']}
          >
            <ButtonContainer
              data-testid="context-menu-button"
              $isOpen={isOpen}
              $hoverbgColor={themeConfig[theme].colors.border}
            >
              <MoreOutlined
                style={{ fontSize: 16, color: themeConfig[theme].colors.text }}
              />
            </ButtonContainer>
          </Dropdown>
        </div>
      </Tooltip>
    );
  },
);
ContextMenu.displayName = 'ContextMenu';

const ButtonContainer = styled.div<{ $isOpen: boolean; $hoverbgColor: string }>`
  width: fit-content;
  line-height: normal;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  background-color: ${({ $isOpen }) =>
    $isOpen ? 'rgba(0, 0, 0, 0.04)' : 'transparent'};
  &:hover {
    background-color: ${({ $hoverbgColor }) => $hoverbgColor};
  }
`;
